// ShopBook Terms of Service and Privacy Policy.
//
// LEGAL_VERSION must match backend/src/config/legal.js. Change it (to the new
// "last updated" date) whenever either document changes materially — every
// user is then asked to read and accept again before continuing.
//
// Before relying on these in a dispute, have them reviewed by a Botswana
// lawyer and fill in OPERATOR below with the registered business details.
import React from 'react';

export const LEGAL_VERSION = '2026-09-26';
export const LAST_UPDATED = '26 September 2026';

// Who provides the service. Fill in the registered name, registration
// number and physical address once available; blank fields are left out.
export const OPERATOR = {
  name: 'ShopBook',
  legalName: '',          // e.g. 'ShopBook (Pty) Ltd'
  registration: '',       // company / business registration number
  address: '',            // physical address in Botswana
  email: 'support@getshopbook.com',
};

const operatorLine = () => {
  const parts = [OPERATOR.legalName || OPERATOR.name];
  if (OPERATOR.registration) parts.push(`registration number ${OPERATOR.registration}`);
  if (OPERATOR.address) parts.push(OPERATOR.address);
  return parts.join(', ');
};

const Mail = () => <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>;

/* ───────────────────────────── Privacy Policy ───────────────────────────── */

export function PrivacyPolicy() {
  return (
    <>
      <p className="legal-meta">Last updated {LAST_UPDATED}</p>

      <p>
        This policy explains what information ShopBook collects, why, who it is shared with, how long it is
        kept and the rights you have. ShopBook is provided by {operatorLine()} ("ShopBook", "we", "us").
        We handle personal information in line with the Data Protection Act of Botswana and the guidance of
        the Information and Data Protection Commission.
      </p>

      <h2>1. Two kinds of information</h2>
      <p>
        <strong>Your account information</strong> — the details of the people who use ShopBook (business
        owners, managers and staff). For this information ShopBook decides how it is used, so we are
        responsible for it (the "data controller").
      </p>
      <p>
        <strong>Your business records</strong> — the products, sales, customers, debts, payments, suppliers
        and expenses you record. These belong to your business. Your business decides what to record about its
        own customers and suppliers, so your business is responsible for that information and we process it
        only on your behalf, to provide the service (we are the "data processor"). We do not use your
        customers' details for our own purposes and we never contact them.
      </p>

      <h2>2. What we collect</h2>
      <ul>
        <li><strong>Account details:</strong> name, mobile number and/or email address, password (stored only as
          a secure one-way hash — we cannot read it), business and company names, country and currency, your
          role, and for staff the permissions the owner gives them.</li>
        <li><strong>Business records you enter:</strong> products and stock, purchases and suppliers, sales,
          the names and contact details of customers you record, amounts owed, payments received, expenses,
          assets and capital.</li>
        <li><strong>Customer ratings:</strong> ShopBook automatically rates each of your customers as good,
          neutral or bad from their payment history in your records, to help you decide on credit. Ratings are
          visible only to your business, are never shared, and ShopBook takes no decisions about your customers
          itself.</li>
        <li><strong>Billing details:</strong> your plan, payment amounts, dates, the payment method you tell us
          (for example Orange Money or EFT) and your payment reference. We do not collect card or bank
          login details — you pay outside the app.</li>
        <li><strong>Usage and security information:</strong> when you sign in, when the app is in use and for
          how long, your IP address, a device identifier used to sync your records, the app version, and
          records of changes made through sync (who made them and when).</li>
        <li><strong>Error reports:</strong> if the app crashes we may receive the error message, the page it
          happened on and your browser type — never the contents of your records.</li>
      </ul>

      <h2>3. Why we use it</h2>
      <ul>
        <li>To provide ShopBook: create and secure your account, store and sync your records, produce your
          reports and exports — because we have a contract with you.</li>
        <li>To send you service messages: verification and password-reset codes, payment receipts and
          important notices about your account.</li>
        <li>To bill you and manage your subscription, and to keep financial records we are required by law
          to keep.</li>
        <li>To keep ShopBook secure and working: preventing fraud and misuse, limiting repeated sign-in
          attempts, finding and fixing errors, and understanding which features are used so we can improve
          them — in our legitimate interest in running a safe, reliable service.</li>
      </ul>
      <p>We do not sell personal information, show advertising, or use it for marketing by anyone else.</p>

      <h2>4. Who we share it with</h2>
      <p>Only the service providers we need to run ShopBook, each bound to protect it and use it only for us:</p>
      <ul>
        <li><strong>Railway</strong> — hosts the ShopBook servers and database.</li>
        <li><strong>Cloudflare</strong> — stores our encrypted nightly database backups.</li>
        <li><strong>Brevo</strong> — sends our emails (codes, receipts, notices).</li>
        <li><strong>Sentry</strong> — receives error reports, when error monitoring is switched on.</li>
      </ul>
      <p>
        Within your own business, the owner can see everything; managers and staff see what the owner's
        permission settings allow. We may disclose information if the law requires it (for example a valid
        court order), and would pass it to a new owner of ShopBook only under the same protections.
      </p>

      <h2>5. Where it is stored</h2>
      <p>
        Our providers' servers are located outside Botswana (including in the European Union and the United
        States). We use only providers that protect personal information to a standard at least equivalent
        to Botswana law, under contracts that require them to keep it secure and confidential.
      </p>

      <h2>6. How we protect it</h2>
      <p>
        All connections to ShopBook are encrypted. Passwords are hashed, sign-in attempts are limited,
        sessions can be revoked, backups are encrypted, and each business can only reach its own records.
        Owners control what each staff member can see and do, and every change is recorded with who made it.
        If a breach affecting your personal information occurs, we will notify the Information and Data
        Protection Commission and affected users as the law requires.
      </p>

      <h2>7. How long we keep it</h2>
      <ul>
        <li>Account and business records: for as long as your account is open.</li>
        <li>When you close your account, we delete or anonymise your account and business records within
          90 days, except information we must keep by law (such as billing records) or to resolve a dispute.
          Please export anything you need first — Settings → Export your data.</li>
        <li>Database backups are overwritten on a rolling basis, within about 30 days.</li>
        <li>Sign-in and usage records: up to 12 months.</li>
      </ul>
      <p>Your business may have its own legal duty to keep financial records for longer — exports let you do that.</p>

      <h2>8. Your rights</h2>
      <p>You may ask us to:</p>
      <ul>
        <li>tell you what personal information we hold about you and give you a copy;</li>
        <li>correct information that is wrong or incomplete (you can change most details yourself in Settings);</li>
        <li>delete your information, or close your account;</li>
        <li>stop or limit certain uses of it, or object to them;</li>
        <li>give you your records in a portable format (Settings → Export your data does this instantly).</li>
      </ul>
      <p>
        Email <Mail /> from the email or phone number on your account. We will reply within 30 days. If you
        are one of our users' customers and want to know what a shop has recorded about you, please ask that
        business first — it controls those records — and we will help it respond.
      </p>
      <p>
        If you are unhappy with how we handle your information, you may complain to the Information and Data
        Protection Commission of Botswana.
      </p>

      <h2>9. Information on your device</h2>
      <p>
        The app keeps your sign-in session and a working copy of your business records on your device so it
        stays fast and keeps working if your signal drops. This copy is removed when you log out. ShopBook
        uses no advertising or tracking cookies.
      </p>

      <h2>10. Children</h2>
      <p>ShopBook is for businesses and is not meant for anyone under 18.</p>

      <h2>11. Changes to this policy</h2>
      <p>
        If we change this policy in a meaningful way we will update the date above and ask you to read and
        accept the new version the next time you open ShopBook.
      </p>

      <h2>12. Contact</h2>
      <p>Questions about privacy: <Mail />.</p>
    </>
  );
}

/* ─────────────────────────── Terms of Service ─────────────────────────── */

export function TermsOfService() {
  return (
    <>
      <p className="legal-meta">Last updated {LAST_UPDATED}</p>

      <p>
        These terms are an agreement between you and {operatorLine()} ("ShopBook", "we", "us") for the use of
        the ShopBook app and website. By creating an account or using ShopBook you agree to them. If you use
        ShopBook for a business, you confirm that you may accept these terms for that business.
      </p>

      <h2>1. Your account</h2>
      <ul>
        <li>You must be at least 18 and give accurate details.</li>
        <li>Keep your password secret. You are responsible for what happens under your account.</li>
        <li>As an owner you may add managers and staff. You decide what they can see and do, and you are
          responsible for their use of ShopBook. Remove access when someone leaves.</li>
        <li>Tell us straight away at <Mail /> if you think someone has used your account without permission.</li>
      </ul>

      <h2>2. What ShopBook does — and does not do</h2>
      <p>
        ShopBook is a record-keeping tool for stock, sales, debts, expenses and simple financial reports. Its
        figures are only as accurate as the records entered into it. It is not accounting, tax, legal or
        financial advice, and it does not replace an accountant. You remain responsible for your business's
        records, tax returns and legal obligations.
      </p>
      <p>
        If your connection drops, records made while the app is open are kept on your device and sent when you
        are back online. Records that have not yet been sent can be lost if you log out, clear the app's data or
        lose the device — the app warns you before logging out.
      </p>

      <h2>3. Your records</h2>
      <ul>
        <li>The records you enter belong to your business. You give us permission to store and process them
          only to provide ShopBook to you.</li>
        <li>You can export your records at any time, including while an account is read-only.</li>
        <li>If you record information about customers, suppliers or staff, you are responsible for having the
          right to do so and for treating it in line with the Data Protection Act. Our{' '}
          <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> explains how we handle it.</li>
      </ul>

      <h2>4. Plans, trial and payment</h2>
      <ul>
        <li>New accounts start with a free 14-day trial of the Starter plan.</li>
        <li>After the trial, ShopBook is a paid service. Current plans and prices (monthly or annual) are shown
          in the app. We may change prices with at least 30 days' notice; a change applies from your next
          billing period.</li>
        <li>You pay by mobile money or bank transfer using your payment reference. Your plan is activated or
          renewed once we confirm your payment, and we email a receipt. We never charge you automatically.</li>
        <li>Upgrading part-way through a paid period credits the unused part of your current plan.</li>
        <li>If a trial or subscription ends without payment, everything keeps working for a 7-day grace
          period. After that the account becomes read-only: you can still sign in, view and export your
          records, but not add or change them until you pay. A lapsed Pro or Enterprise account moves to
          Starter.</li>
        <li>Payments are for the period paid and are not refundable, except where the law requires a refund or
          we fail to provide the service you paid for.</li>
      </ul>

      <h2>5. Acceptable use</h2>
      <p>You must not:</p>
      <ul>
        <li>use ShopBook for anything unlawful or fraudulent;</li>
        <li>try to access another business's records, get around permissions or security, or disrupt the service;</li>
        <li>copy, resell or reverse-engineer ShopBook, or access it by automated means we have not approved;</li>
        <li>share your sign-in with people outside your business instead of adding them as team members.</li>
      </ul>

      <h2>6. Availability and changes</h2>
      <p>
        We work to keep ShopBook available and back it up every night, but we cannot promise it will always be
        uninterrupted or error-free. We may need to pause it for maintenance, and we may improve or change
        features. Keeping your own exports is a sensible precaution.
      </p>

      <h2>7. Suspension and closing an account</h2>
      <ul>
        <li>You can stop using ShopBook at any time and ask us to close your account at <Mail />. Export your
          records first; our Privacy Policy explains when they are deleted.</li>
        <li>We may suspend or close an account that breaks these terms, is used unlawfully, or puts other users
          or the service at risk. Where reasonable we will warn you first and give you a chance to export your
          records.</li>
      </ul>

      <h2>8. Our responsibility</h2>
      <p>
        ShopBook is provided as it is. To the extent the law allows, we are not responsible for indirect or
        consequential losses (such as lost profit or business decisions made on the app's figures), and our
        total responsibility to you is limited to the amount you paid us in the 12 months before the claim.
        Nothing in these terms limits rights you have under the Consumer Protection Act or any other law that
        cannot be excluded.
      </p>

      <h2>9. Changes to these terms</h2>
      <p>
        If we change these terms in a meaningful way we will update the date above and ask you to read and
        accept the new version the next time you open ShopBook. If you do not agree, you can stop using
        ShopBook and export your records.
      </p>

      <h2>10. Law and disputes</h2>
      <p>
        These terms are governed by the laws of Botswana, and the courts of Botswana have jurisdiction. Please
        contact us first — most problems can be sorted out quickly.
      </p>

      <h2>11. Contact</h2>
      <p>{operatorLine()} · <Mail /></p>
    </>
  );
}
